'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { ChevronLeft, ChevronRight, Star, Quote, Play, CheckCircle } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function TestimonialsSection() {
    const { t, isLoading } = useTranslation('root-canal');
    const { isRTL } = useRTL();
    const [currentSlide, setCurrentSlide] = useState(0);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const carouselRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 85%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            gsap.fromTo(carouselRef.current,
                { opacity: 0, y: 60 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: carouselRef.current,
                        start: "top 85%",
                        toggleActions: "play none none reverse"
                    }
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helper function to ensure results are always an array
    const getResultsArray = (results: unknown): string[] => {
        if (Array.isArray(results)) {
            return results.filter((item): item is string => typeof item === 'string');
        }
        if (typeof results === 'string') {
            return [results];
        }
        return [];
    };

    const testimonials = [
        {
            id: 1,
            name: t('testimonials.patients.sarah.name'),
            age: t('testimonials.patients.sarah.age'),
            country: t('testimonials.patients.sarah.country'),
            treatment: t('testimonials.patients.sarah.treatment'),
            rating: 5,
            quote: t('testimonials.patients.sarah.quote'),
            beforeImage: "/images/treatments/root-canal/sarah-before.webp",
            afterImage: "/images/treatments/root-canal/sarah-after.webp",
            portrait: "/images/treatments/root-canal/sarah-portrait.webp",
            videoTestimonial: true,
            results: getResultsArray(t('testimonials.patients.sarah.results', { returnObjects: true }))
        }
    ];

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const goToSlide = (index: number) => {
        setCurrentSlide(index);
    };

    const currentTestimonial = testimonials[currentSlide];

    return (
        <section id="testimonials" ref={sectionRef} className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    {isLoading ? (
                        <div className="animate-pulse">
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-2xl mx-auto"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif mb-6">
                                {t('testimonials.title.part1')} <span className="text-gold">{t('testimonials.title.part2')}</span>
                            </h2>
                            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                                {t('testimonials.subtitle')}
                            </p>
                        </>
                    )}
                </div>

                <div ref={carouselRef} className="max-w-6xl mx-auto">
                    {/* Main Testimonial Card */}
                    <Card className="border-2 border-slate-200 dark:border-slate-600 hover:border-gold transition-all duration-300 shadow-xl">
                        <CardContent className="p-0">
                            <div className="grid lg:grid-cols-2 gap-0">
                                {/* Testimonial Content */}
                                <div className="p-8 bg-white dark:bg-slate-800">
                                    {/* Patient Info */}
                                    <div className={`flex items-center gap-4 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <div className={`${isRTL ? 'text-right' : 'text-left'} flex-1`}>
                                            <h3 className={`font-bold text-navy dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>
                                                {currentTestimonial.name}
                                            </h3>
                                            <p className={`text-slate-600 dark:text-slate-300 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                                                {t('testimonials.labels.age')} {currentTestimonial.age} • {currentTestimonial.country}
                                            </p>
                                        </div>
                                        <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0 overflow-hidden">
                                            {currentTestimonial.portrait ? (
                                                <OptimizedImage
                                                    src={currentTestimonial.portrait}
                                                    alt={currentTestimonial.name}
                                                    fill
                                                    sizes="64px"
                                                />
                                            ) : (
                                                currentTestimonial.name.split(' ')[0][0]
                                            )}
                                        </div>
                                    </div>

                                    {/* Rating */}
                                    <div className={`flex items-center gap-1 mb-6 ${isRTL ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
                                        <span className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'ml-2' : 'mr-2'}`}>
                                            ({currentTestimonial.rating}/5)
                                        </span>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <Star
                                                key={index}
                                                className={`w-5 h-5 ${index < currentTestimonial.rating
                                                    ? 'text-gold fill-current'
                                                    : 'text-slate-200 dark:text-slate-600'
                                                    }`}
                                            />
                                        ))}
                                    </div>

                                    {/* Quote */}
                                    <div className="relative mb-6">
                                        <Quote className="w-8 h-8 text-gold mb-4" />
                                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-lg italic">
                                            "{currentTestimonial.quote}"
                                        </p>
                                    </div>

                                    {/* Video Testimonial Button */}
                                    {currentTestimonial.videoTestimonial && (
                                        <Button
                                            variant="outline"
                                            className="w-full border-gold text-gold hover:bg-gold hover:text-white"
                                        >
                                            <Play className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                            {t('testimonials.labels.videoButton')}
                                        </Button>
                                    )}
                                </div>

                                {/* Before & After Images */}
                                <div className="p-8 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
                                    <div className="text-center mb-6">
                                        <h3 className="text-xl font-bold text-navy dark:text-white mb-2">
                                            {t('testimonials.beforeAfterTitle')}
                                        </h3>
                                        <p className="text-slate-600 dark:text-slate-300 text-sm">
                                            {currentTestimonial.treatment}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="relative">
                                            <div className="absolute top-2 left-2 z-10 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                                {t('testimonials.labels.before')}
                                            </div>
                                            <div className="relative aspect-square bg-slate-200 dark:bg-slate-600 rounded-xl overflow-hidden">
                                                {currentTestimonial.beforeImage ? (
                                                    <OptimizedImage
                                                        src={currentTestimonial.beforeImage}
                                                        alt={`${currentTestimonial.name} - ${t('testimonials.labels.before')}`}
                                                        fill
                                                        sizes="(max-width: 1024px) 50vw, 25vw"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-slate-600 dark:text-slate-300">
                                                        {t('testimonials.labels.before')} Image
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <div className="absolute top-2 left-2 z-10 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                                {t('testimonials.labels.after')}
                                            </div>
                                            <div className="relative aspect-square bg-slate-200 dark:bg-slate-600 rounded-xl overflow-hidden">
                                                {currentTestimonial.afterImage ? (
                                                    <OptimizedImage
                                                        src={currentTestimonial.afterImage}
                                                        alt={`${currentTestimonial.name} - ${t('testimonials.labels.after')}`}
                                                        fill
                                                        sizes="(max-width: 1024px) 50vw, 25vw"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-slate-600 dark:text-slate-300">
                                                        {t('testimonials.labels.after')} Image
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Results */}
                                    <div className="space-y-2">
                                        <h4 className={`font-semibold text-navy dark:text-white mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('testimonials.resultsTitle')}</h4>
                                        {currentTestimonial.results.map((result, index) => (
                                            <div key={index} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                                <span className={`text-sm text-slate-600 dark:text-slate-300 flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>{result}</span>
                                                <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Navigation — only when there is more than one testimonial */}
                    {testimonials.length > 1 && (
                        <div className="flex items-center justify-between mt-8">
                            <Button
                                variant="outline"
                                onClick={prevSlide}
                                className="border-gold text-gold hover:bg-gold hover:text-white"
                            >
                                <ChevronLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                                {t('testimonials.labels.previous')}
                            </Button>

                            <div className="flex gap-2">
                                {testimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => goToSlide(index)}
                                        className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide
                                            ? 'bg-gold scale-125'
                                            : 'bg-slate-200 dark:bg-slate-600 hover:bg-gold/50'
                                            }`}
                                    />
                                ))}
                            </div>

                            <Button
                                variant="outline"
                                onClick={nextSlide}
                                className="border-gold text-gold hover:bg-gold hover:text-white"
                            >
                                {t('testimonials.labels.next')}
                                <ChevronRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                            </Button>
                        </div>
                    )}

                    {/* Bottom stats */}
                    <div className="grid grid-cols-3 gap-6 mt-12 max-w-2xl mx-auto">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gold mb-2">{t('testimonials.stats.procedures.number')}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-300">{t('testimonials.stats.procedures.label')}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gold mb-2">{t('testimonials.stats.successRate.number')}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-300">{t('testimonials.stats.successRate.label')}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gold mb-2">{t('testimonials.stats.rating.number')}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-300">{t('testimonials.stats.rating.label')}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 